/**
 * follows.ts - ALL follow logic in ONE file
 *
 * Contains:
 * - Data types (FollowItem)
 * - Browser storage (localStorage via PerAccountLocalStorage)
 * - File storage (Desktop) - separate public/private files
 * - Relay operations (NIP-02 kind:3 + NIP-51 kind:30000 for private)
 * - FollowStorageAdapter (for AutoSyncService/ListSyncManager)
 * - FollowOrchestrator (legacy alias for backward compatibility)
 * - ProfileFollowManager (profile follow/unfollow UI)
 * - FollowListManager (sidebar list UI)
 *
 * NOTE: This is the MOST CRITICAL list - the timeline depends on it!
 *
 * GOAL: Claude can understand and fix follow bugs without navigating between files.
 */

import { StorageKeys, now, deduplicateByPubkey, mergeByKey } from './storage';
import {
  readJsonFile,
  writeJsonFile,
  uploadJsonFile,
  downloadAsJson,
} from './file';
import {
  fetchEvents,
  publishEvent,
  signEvent,
  requireAuth,
  getCurrentUserPubkey,
} from './relays';
import { escapeHtml, escapeHtmlAttr } from '../helpers/escapeHtml';
import { PerAccountLocalStorage } from '../services/PerAccountLocalStorage';
import { SystemLogger } from '../services/SystemLogger';
import { TypedEventBus } from '../core/TypedEventBus';
import { AuthService } from '../services/AuthService';
import { diagLog } from '../services/DiagnosticLogger';
import { ToastService } from '../services/ToastService';
import { AppState } from '../services/AppState';
import type { MutualState } from '../services/FollowVerificationService';
import { SyncConfirmationModal } from '../components/modals/SyncConfirmationModal';
import { switchTabWithContent } from '../helpers/TabsHelper';
import { getSccDefaultTab } from '../helpers/sccDefaultTab';
import {
  renderListSyncButtons,
  bindListSyncButtons,
} from '../helpers/ListSyncMode';
import { PlatformService } from '../services/PlatformService';
import {
  UserProfileService,
  type UserProfile,
} from '../services/UserProfileService';
import { UserService } from '../services/UserService';
import { FollowerCountService } from '../services/FollowerCountService';
import type { FollowsExtendedFeatures } from './follows-extended';
import { isExtendedFollowsEnabled } from '../addons/extended-follows/index';
import { Router } from '../services/Router';
import { hexToNpub } from '../helpers/nip19';
import { extractDisplayName } from '../helpers/extractDisplayName';
import { InfiniteScroll } from '../components/ui/InfiniteScroll';
import { ProgressBarHelper } from '../helpers/ProgressBarHelper';
import { ArticleNotificationService } from '../services/ArticleNotificationService';

const logger = SystemLogger.getInstance();
const eventBus = TypedEventBus.getInstance();

// ============================================================
// TYPES
// ============================================================

/**
 * Follow item with NIP-02 metadata
 */
export interface FollowItem {
  id: string; // Same as pubkey (for BaseListItem compatibility)
  pubkey: string;
  relay?: string; // NIP-02: Optional relay hint
  petname?: string; // NIP-02: Optional local nickname
  addedAt?: number; // Timestamp when added
  isPrivate?: boolean; // True if this is a private follow
}

/**
 * File storage format
 */
export interface FollowListData {
  items: FollowItem[];
  lastModified: number;
}

/**
 * Result from fetching follows from relays
 */
export interface FetchFromRelaysResult {
  items: FollowItem[];
  relayContentWasEmpty: boolean;
  decryptionFailed?: boolean;
  relayTimestamp: number;
}

/**
 * Sync diff between browser and relay/file
 */
interface SyncDiff {
  added: FollowItem[];
  removed: FollowItem[];
  unchanged: FollowItem[];
}

// ============================================================
// FULL STATE COMPARISON (checks ALL differences)
// Order is NOT compared - follows are displayed sorted by addedAt
// ============================================================

/**
 * Check if browser and source follow lists are different.
 * Compares: same pubkeys exist, same properties per pubkey (petname, isPrivate)
 * Does NOT compare order (user can't reorder, displayed by date)
 */
function hasFollowDifference(
  browserItems: FollowItem[],
  sourceItems: FollowItem[]
): boolean {
  diagLog('lists', 'hasFollowDifference', {
    browserCount: browserItems.length,
    sourceCount: sourceItems.length,
    browserPubkeys: browserItems.map(i => i.pubkey.slice(0, 8)),
    sourcePubkeys: sourceItems.map(i => i.pubkey.slice(0, 8)),
  });
  // Different count = different
  if (browserItems.length !== sourceItems.length) return true;

  // Build maps for property comparison
  const browserMap = new Map<string, FollowItem>();
  for (const item of browserItems) {
    browserMap.set(item.pubkey, item);
  }

  const sourceMap = new Map<string, FollowItem>();
  for (const item of sourceItems) {
    sourceMap.set(item.pubkey, item);
  }

  // Check if same pubkeys exist
  for (const pubkey of browserMap.keys()) {
    if (!sourceMap.has(pubkey)) return true;
  }
  for (const pubkey of sourceMap.keys()) {
    if (!browserMap.has(pubkey)) return true;
  }

  // Check if properties match for each pubkey
  for (const [pubkey, browserItem] of browserMap) {
    const sourceItem = sourceMap.get(pubkey);
    if (!sourceItem) return true;

    // Compare petname
    const browserPetname = browserItem.petname || '';
    const sourcePetname = sourceItem.petname || '';
    if (browserPetname !== sourcePetname) return true;

    // Compare isPrivate
    const browserPrivate = browserItem.isPrivate || false;
    const sourcePrivate = sourceItem.isPrivate || false;
    if (browserPrivate !== sourcePrivate) return true;
  }

  return false;
}

/**
 * Result from sync from relays (phase 1)
 */
interface SyncFromRelaysResult {
  requiresConfirmation: boolean;
  diff: SyncDiff;
  relayItems: FollowItem[];
  relayContentWasEmpty: boolean;
  relayTimestamp: number;
}

/**
 * Result from sync from file (phase 1)
 */
interface SyncFromFileResult {
  requiresConfirmation: boolean;
  diff: SyncDiff;
  fileItems: FollowItem[];
}

/**
 * Follow item with profile (for UI rendering)
 */
interface FollowItemWithProfile {
  pubkey: string;
  relay?: string;
  petname?: string;
  addedAt?: number;
  profile: UserProfile;
  isPrivate: boolean;
  /** Canonical tri-state "does this user follow me back?" — see FollowVerificationService. */
  mutualState: MutualState;
}

// ============================================================
// BROWSER STORAGE (localStorage via PerAccountLocalStorage)
// ============================================================

const storage = PerAccountLocalStorage.getInstance();

/**
 * Read follow items from browser storage
 */
export function getFollowItems(): FollowItem[] {
  return storage.get<FollowItem[]>(StorageKeys.FOLLOWS, []);
}

/**
 * Write follow items to browser storage
 */
export function setFollowItems(items: FollowItem[]): void {
  storage.set(StorageKeys.FOLLOWS, items);
  eventBus.emit('follow:updated');
}

/**
 * Clear follow items from browser storage
 */
export function clearFollowItems(): void {
  storage.remove(StorageKeys.FOLLOWS);
}

/**
 * Get all followed pubkeys
 */
export function getAllFollowedPubkeys(): string[] {
  return getFollowItems().map(item => item.pubkey);
}

/**
 * Get all follows with their public/private status
 */
export function getAllFollowsWithStatus(): Map<
  string,
  { public: boolean; private: boolean }
> {
  const items = getFollowItems();
  const statusMap = new Map<string, { public: boolean; private: boolean }>();

  for (const item of items) {
    const existing = statusMap.get(item.pubkey);
    const isPrivate = item.isPrivate === true;

    if (existing) {
      existing.private = existing.private || isPrivate;
      existing.public = existing.public || !isPrivate;
    } else {
      statusMap.set(item.pubkey, { public: !isPrivate, private: isPrivate });
    }
  }

  return statusMap;
}

/**
 * Check if a user is followed
 */
export function isFollowing(pubkey: string): {
  public: boolean;
  private: boolean;
} {
  const items = getFollowItems().filter(item => item.pubkey === pubkey);

  if (items.length === 0) {
    return { public: false, private: false };
  }

  return {
    public: items.some(item => !item.isPrivate),
    private: items.some(item => item.isPrivate === true),
  };
}

/**
 * Read the NIP-02 petname (4th p-tag element) for a publicly-followed user.
 * Sync, from local storage. Returns null if not publicly followed or unset.
 */
export function getFollowPetname(pubkey: string): string | null {
  const item = getFollowItems().find(i => i.pubkey === pubkey && !i.isPrivate);
  return item?.petname ?? null;
}

/**
 * Set or clear the NIP-02 petname for a publicly-followed user.
 *
 * NIP-02 petnames only exist as the 4th element of a contact-list p-tag, so we
 * NEVER create a follow just to attach a label — if the user isn't publicly
 * followed this is a no-op returning false. Empty/blank clears the petname.
 *
 * Mirrors the unfollow path: updates local storage (which schedules the Easy-Mode
 * sync via follow:updated) and then republishes immediately to bypass the debounce.
 * publishToRelays rebuilds the full kind:3 from local items, so all other follow
 * data (other follows, relays, petnames) is preserved.
 *
 * @returns true if updated, false if the user isn't publicly followed.
 */
export async function setFollowPetname(
  pubkey: string,
  petname: string
): Promise<boolean> {
  requireAuth();

  const items = getFollowItems();
  const item = items.find(i => i.pubkey === pubkey && !i.isPrivate);
  if (!item) return false;

  const trimmed = petname.trim();
  if (trimmed) {
    item.petname = trimmed;
  } else {
    delete item.petname;
  }

  setFollowItems(items);

  try {
    await publishToRelays();
    diagLog('lists', 'setFollowPetname: published', {
      pubkey: pubkey.slice(0, 8),
      hasPetname: !!trimmed,
    });
  } catch (err) {
    diagLog('lists', 'setFollowPetname: immediate publish failed', {
      error: String(err),
    });
  }
  return true;
}

/**
 * Follow a user
 */
export function followUser(
  pubkey: string,
  isPrivate: boolean = false,
  relay?: string,
  petname?: string
): void {
  requireAuth();

  const items = getFollowItems();

  // Check if already following
  const existingIndex = items.findIndex(item => item.pubkey === pubkey);

  if (existingIndex !== -1) {
    // Update existing follow
    const existing = items[existingIndex];
    if (existing) {
      existing.isPrivate = isPrivate;
      if (relay !== undefined) existing.relay = relay;
      if (petname !== undefined) existing.petname = petname;
    }
  } else {
    // Add new follow
    const newItem: FollowItem = {
      id: pubkey,
      pubkey,
      isPrivate,
      addedAt: now(),
    };
    if (relay) newItem.relay = relay;
    if (petname) newItem.petname = petname;
    items.push(newItem);
  }

  setFollowItems(items);
  logger.info(
    'follows.ts',
    `Followed user ${pubkey.slice(0, 8)}... (${isPrivate ? 'private' : 'public'})`
  );
}

/**
 * Unfollow a user
 */
export function unfollowUser(pubkey: string): void {
  requireAuth();

  const items = getFollowItems();
  const filtered = items.filter(item => item.pubkey !== pubkey);

  if (filtered.length !== items.length) {
    setFollowItems(filtered);
    logger.info('follows.ts', `Unfollowed user ${pubkey.slice(0, 8)}...`);
  }
}

// ============================================================
// SETTINGS
// ============================================================

/**
 * Check if private follows feature is enabled
 */
export function isPrivateFollowsEnabled(): boolean {
  return storage.get<boolean>(StorageKeys.PRIVATE_FOLLOWS_ENABLED, false);
}

/**
 * Set private follows feature flag
 */
export function setPrivateFollowsEnabled(enabled: boolean): void {
  storage.set(StorageKeys.PRIVATE_FOLLOWS_ENABLED, enabled);
}

/**
 * Check if migrated to file storage
 */
export function isMigratedToFileStorage(): boolean {
  return storage.get<boolean>(StorageKeys.FOLLOWS_FILE_MIGRATION, false);
}

/**
 * Set migration flag
 */
export function setMigratedToFileStorage(): void {
  storage.set(StorageKeys.FOLLOWS_FILE_MIGRATION, true);
}

// ============================================================
// FILE STORAGE (Desktop)
// ============================================================

const PUBLIC_FOLLOWS_FILE = 'follows-public.json';
const PRIVATE_FOLLOWS_FILE = 'follows-private.json';

function createEmptyFollowListData(): FollowListData {
  return {
    items: [],
    lastModified: now(),
  };
}

/**
 * Read public follows from file
 */
export async function readPublicFollowsFile(): Promise<FollowListData> {
  return readJsonFile<FollowListData>(
    PUBLIC_FOLLOWS_FILE,
    createEmptyFollowListData()
  );
}

/**
 * Read private follows from file
 */
export async function readPrivateFollowsFile(): Promise<FollowListData> {
  return readJsonFile<FollowListData>(
    PRIVATE_FOLLOWS_FILE,
    createEmptyFollowListData()
  );
}

/**
 * Write public follows to file
 */
export async function writePublicFollowsFile(
  data: FollowListData
): Promise<void> {
  data.lastModified = now();
  await writeJsonFile(PUBLIC_FOLLOWS_FILE, data);
}

/**
 * Write private follows to file
 */
export async function writePrivateFollowsFile(
  data: FollowListData
): Promise<void> {
  data.lastModified = now();
  await writeJsonFile(PRIVATE_FOLLOWS_FILE, data);
}

/**
 * Save current browser state to files
 */
export async function saveToFile(): Promise<void> {
  const items = getFollowItems();

  const publicFollows = items.filter(i => !i.isPrivate);
  const privateFollows = items.filter(i => i.isPrivate === true);

  await writePublicFollowsFile({
    items: publicFollows,
    lastModified: now(),
  });

  await writePrivateFollowsFile({
    items: privateFollows,
    lastModified: now(),
  });

  logger.info(
    'follows.ts',
    `Saved to files: ${publicFollows.length} public, ${privateFollows.length} private`
  );
}

/**
 * Restore from files to browser storage
 */
export async function restoreFromFile(): Promise<void> {
  const publicData = await readPublicFollowsFile();
  const privateData = await readPrivateFollowsFile();

  // Mark items with their privacy status and deduplicate
  const allItems = deduplicateByPubkey([
    ...publicData.items.map(item => ({ ...item, isPrivate: false })),
    ...privateData.items.map(item => ({ ...item, isPrivate: true })),
  ]);

  setFollowItems(allItems);
  logger.info('follows.ts', `Restored from files: ${allItems.length} items`);
}

/**
 * Get all follows from file (for RestoreListsService)
 */
export async function getFileFollows(): Promise<FollowItem[]> {
  const publicData = await readPublicFollowsFile();
  const privateData = await readPrivateFollowsFile();

  return deduplicateByPubkey([
    ...publicData.items.map(item => ({ ...item, isPrivate: false })),
    ...privateData.items.map(item => ({ ...item, isPrivate: true })),
  ]);
}

// ============================================================
// RELAY OPERATIONS (NIP-02 kind:3 + NIP-51 kind:30000)
// ============================================================

/**
 * Fetch follows from relays
 * - kind:3 for public follows (NIP-02)
 * - kind:30000 with d-tag "private-follows" for private follows (NIP-51)
 */
export async function fetchFromRelays(): Promise<FetchFromRelaysResult> {
  const pubkey = getCurrentUserPubkey();
  if (!pubkey) {
    return { items: [], relayContentWasEmpty: true, relayTimestamp: 0 };
  }

  try {
    // Fetch both kind:3 (public) and kind:30000 (private) events
    // skipCache=true for sync operations
    const [kind3Events, kind30000Events] = await Promise.all([
      fetchEvents(
        [
          {
            authors: [pubkey],
            kinds: [3],
            limit: 1,
          },
        ],
        10000,
        true
      ),
      isPrivateFollowsEnabled()
        ? fetchEvents(
            [
              {
                authors: [pubkey],
                kinds: [30000],
                '#d': ['private-follows'],
                limit: 1,
              },
            ],
            10000,
            true
          )
        : Promise.resolve([]),
    ]);

    const items: FollowItem[] = [];
    let decryptionFailed = false;

    // Extract public follows from kind:3 tags (most recent event)
    const kind3Event = kind3Events.reduce<(typeof kind3Events)[0] | undefined>(
      (latest, ev) =>
        !latest || ev.created_at > latest.created_at ? ev : latest,
      undefined
    );
    if (kind3Event) {
      for (const tag of kind3Event.tags) {
        if (tag[0] === 'p' && tag[1]) {
          items.push({
            id: tag[1],
            pubkey: tag[1],
            addedAt: kind3Event.created_at,
            isPrivate: false,
            ...(tag[2] && { relay: tag[2] }),
            ...(tag[3] && { petname: tag[3] }),
          });
        }
      }
    }

    // Extract private follows from kind:30000 content (most recent event)
    if (isPrivateFollowsEnabled()) {
      const kind30000Event = kind30000Events.reduce<
        (typeof kind30000Events)[0] | undefined
      >(
        (latest, ev) =>
          !latest || ev.created_at > latest.created_at ? ev : latest,
        undefined
      );
      if (kind30000Event?.content) {
        diagLog(
          'lists',
          'follows fetchFromRelays: kind:30000 event has encrypted content, decrypting',
          { contentLength: kind30000Event.content.length }
        );
        try {
          const { decryptPrivateFollows } = await import(
            '../helpers/decryptPrivateFollows'
          );
          const privatePubkeys = await decryptPrivateFollows(
            kind30000Event.content,
            pubkey
          );
          const timestamp = kind30000Event.created_at;

          for (const pk of privatePubkeys) {
            items.push({
              id: pk,
              pubkey: pk,
              addedAt: timestamp,
              isPrivate: true,
            });
          }
          diagLog(
            'lists',
            'follows fetchFromRelays: decrypted private follows',
            { count: privatePubkeys.length }
          );
        } catch (error) {
          diagLog(
            'lists',
            'follows fetchFromRelays: DECRYPT FAILED for private follows',
            { error: String(error) }
          );
          logger.error(
            'follows.ts',
            `Failed to decrypt private follows: ${String(error)}`
          );
          decryptionFailed = true;
        }
      } else {
        diagLog(
          'lists',
          'follows fetchFromRelays: kind:30000 event has no content (no private follows)'
        );
      }
    }

    // Deduplicate
    const deduped = deduplicateByPubkey(items);

    diagLog('lists', 'follows fetchFromRelays result', {
      totalBeforeDedup: items.length,
      afterDedup: deduped.length,
      publicCount: deduped.filter(i => !i.isPrivate).length,
      privateCount: deduped.filter(i => i.isPrivate).length,
      decryptionFailed,
      pubkeys: deduped.map(i => i.pubkey.slice(0, 8)),
    });
    logger.info('follows.ts', `Fetched from relays: ${deduped.length} items`);

    // Compute relay timestamp from the event(s) created_at
    const eventTimestamp = Math.max(
      kind3Event?.created_at || 0,
      kind30000Events.length > 0
        ? kind30000Events.reduce<(typeof kind30000Events)[0] | undefined>(
            (latest, ev) =>
              !latest || ev.created_at > latest.created_at ? ev : latest,
            undefined
          )?.created_at || 0
        : 0
    );

    return {
      items: deduped,
      relayContentWasEmpty: items.length === 0,
      decryptionFailed,
      relayTimestamp: eventTimestamp,
    };
  } catch (error) {
    logger.error('follows.ts', `Failed to fetch from relays: ${String(error)}`);
    return { items: [], relayContentWasEmpty: true, relayTimestamp: 0 };
  }
}

/**
 * Publish follows to relays
 * - kind:3 for public follows (tags)
 * - kind:30000 for private follows (encrypted content)
 */
export async function publishToRelays(): Promise<void> {
  const user = requireAuth();
  const items = getFollowItems();

  // Separate public and private
  const publicItems = items.filter(item => !item.isPrivate);
  const privateItems = items.filter(item => item.isPrivate === true);
  diagLog('lists', 'follows publishToRelays', {
    totalItems: items.length,
    publicCount: publicItems.length,
    privateCount: privateItems.length,
    publicPubkeys: publicItems.map(i => i.pubkey.slice(0, 8)),
    privatePubkeys: privateItems.map(i => i.pubkey.slice(0, 8)),
  });

  // Build kind:3 tags: ['p', pubkey, relay, petname] per NIP-02.
  // The petname is the 4th element, so the relay slot MUST be present whenever a
  // petname exists (empty string is allowed by NIP-02). A naive conditional spread
  // would drop the relay slot for petname-without-relay and collapse the petname
  // into the relay position — non-conformant and breaks round-tripping.
  const publicTags: string[][] = publicItems.map(item => {
    const tag = ['p', item.pubkey];
    if (item.petname) {
      tag.push(item.relay ?? '', item.petname);
    } else if (item.relay) {
      tag.push(item.relay);
    }
    return tag;
  });

  // Build and publish kind:3 event (public follows)
  const kind3Event = {
    kind: 3,
    created_at: now(),
    tags: publicTags,
    content: '', // Empty content for standard NIP-02
    pubkey: user.pubkey,
  };

  const signedKind3 = await signEvent(kind3Event);
  if (!signedKind3) {
    throw new Error('Failed to sign kind:3 follow list event');
  }

  await publishEvent(signedKind3);
  logger.info(
    'follows.ts',
    `Published kind:3 to relays: ${publicItems.length} public follows`
  );

  // Publish kind:30000 event for private follows (if feature enabled)
  if (isPrivateFollowsEnabled() && privateItems.length > 0) {
    diagLog(
      'lists',
      'follows publishToRelays: encrypting private follows for kind:30000',
      { count: privateItems.length }
    );
    try {
      const { encryptPrivateFollows } = await import(
        '../helpers/encryptPrivateFollows'
      );
      const privatePubkeys = privateItems.map(item => item.pubkey);
      const encryptedContent = await encryptPrivateFollows(
        privatePubkeys,
        user.pubkey
      );
      diagLog('lists', 'follows publishToRelays: encrypted content', {
        contentLength: encryptedContent.length,
      });

      const kind30000Event = {
        kind: 30000,
        created_at: now(),
        tags: [['d', 'private-follows']],
        content: encryptedContent,
        pubkey: user.pubkey,
      };

      const signedKind30000 = await signEvent(kind30000Event);
      if (signedKind30000) {
        await publishEvent(signedKind30000);
        logger.info(
          'follows.ts',
          `Published kind:30000 to relays: ${privateItems.length} private follows`
        );
      }
    } catch (error) {
      logger.error(
        'follows.ts',
        `Failed to publish private follows: ${String(error)}`
      );
      throw error;
    }
  }
}

// ============================================================
// SYNC HELPERS (used by FollowStorageAdapter and FollowListManager)
// ============================================================

function calculateFollowSyncDiff(
  browserItems: FollowItem[],
  relayItems: FollowItem[],
  preservePrivateItems: boolean = false
): SyncDiff {
  const browserIds = new Set(browserItems.map(item => item.pubkey));
  const relayIds = new Set(relayItems.map(item => item.pubkey));

  const added = relayItems.filter(item => !browserIds.has(item.pubkey));
  const removed = browserItems.filter(item => {
    if (!relayIds.has(item.pubkey)) {
      if (preservePrivateItems && item.isPrivate === true) {
        return false;
      }
      return true;
    }
    return false;
  });
  const unchanged = browserItems.filter(item => relayIds.has(item.pubkey));

  diagLog('lists', 'calculateFollowSyncDiff', {
    browserCount: browserItems.length,
    relayCount: relayItems.length,
    preservePrivateItems,
    added: added.length,
    removed: removed.length,
    unchanged: unchanged.length,
    addedPubkeys: added.map(i => i.pubkey.slice(0, 8)),
    removedPubkeys: removed.map(i => i.pubkey.slice(0, 8)),
  });

  return { added, removed, unchanged };
}

function mergeFollowItems(
  browserItems: FollowItem[],
  newItems: FollowItem[]
): FollowItem[] {
  return mergeByKey(browserItems, newItems, 'pubkey');
}

// ============================================================
// FOLLOW STORAGE ADAPTER (self-contained, no external dependencies)
// ============================================================

/**
 * Storage adapter for sync operations
 * All methods are self-contained - no external base class
 */
export class FollowStorageAdapter {
  getItemId(item: FollowItem): string {
    return item.pubkey;
  }

  getBrowserItems(): FollowItem[] {
    return getFollowItems();
  }

  setBrowserItems(items: FollowItem[]): void {
    setFollowItems(items);
  }

  async getFileItems(): Promise<FollowItem[]> {
    return getFileFollows();
  }

  async setFileItems(_items: FollowItem[]): Promise<void> {
    await saveToFile();
  }

  async fetchFromRelays(): Promise<FetchFromRelaysResult> {
    return fetchFromRelays();
  }

  async publishToRelays(_items: FollowItem[]): Promise<void> {
    await publishToRelays();
  }

  // Sync helper methods (for AutoSyncService)
  async syncFromRelays(): Promise<SyncFromRelaysResult> {
    // Snapshot browser state BEFORE fetch (fetch takes 2-10s, user could change list meanwhile)
    const browserItems = this.getBrowserItems();
    diagLog('lists', 'FollowStorageAdapter.syncFromRelays: browserItems', {
      count: browserItems.length,
      pubkeys: browserItems.map(i => i.pubkey.slice(0, 8)),
    });
    const fetchResult = await this.fetchFromRelays();
    const relayItems = fetchResult.items;
    const relayContentWasEmpty = fetchResult.relayContentWasEmpty;
    const decryptionFailed = fetchResult.decryptionFailed || false;
    diagLog('lists', 'FollowStorageAdapter.syncFromRelays: fetchResult', {
      relayItemCount: relayItems.length,
      relayContentWasEmpty,
      decryptionFailed,
    });

    const preservePrivateItems = relayContentWasEmpty || decryptionFailed;
    const diff = calculateFollowSyncDiff(
      browserItems,
      relayItems,
      preservePrivateItems
    );

    // Use full state comparison (checks ALL differences including order and properties)
    const requiresConfirmation = hasFollowDifference(browserItems, relayItems);
    diagLog('lists', 'FollowStorageAdapter.syncFromRelays: result', {
      requiresConfirmation,
      added: diff.added.length,
      removed: diff.removed.length,
      unchanged: diff.unchanged.length,
      preservePrivateItems,
    });

    return {
      requiresConfirmation,
      diff,
      relayItems,
      relayContentWasEmpty: preservePrivateItems,
      relayTimestamp: fetchResult.relayTimestamp,
    };
  }

  applySyncFromRelays(
    strategy: 'merge' | 'overwrite',
    relayItems: FollowItem[],
    relayContentWasEmpty: boolean = false
  ): void {
    const browserItems = this.getBrowserItems();

    if (strategy === 'overwrite') {
      if (relayContentWasEmpty) {
        const localPrivateItems = browserItems.filter(
          item => item.isPrivate === true
        );
        this.setBrowserItems([...relayItems, ...localPrivateItems]);
      } else {
        this.setBrowserItems(relayItems);
      }
    } else {
      this.setBrowserItems(mergeFollowItems(browserItems, relayItems));
    }
  }
}

// ============================================================
// FOLLOW ORCHESTRATOR (Legacy alias for backward compatibility)
// ============================================================

// Sync state tracking (Race Condition Prevention)
let isSyncing = false;
let lastSyncedFollowCount = 0;
let lastSyncTimestamp = 0;

/**
 * FollowListOrchestrator-compatible interface
 * This allows existing code to keep working while we migrate
 */
export const FollowOrchestrator = {
  getInstance: () => ({
    // Settings
    isPrivateFollowsEnabled,
    setPrivateFollowsEnabled,

    // Status
    isSyncInProgress: () => isSyncing,
    getLastSyncedFollowCount: () => lastSyncedFollowCount,
    getLastSyncTimestamp: () => lastSyncTimestamp,

    // Follow operations
    addFollow: async (pubkey: string, isPrivate: boolean) =>
      followUser(pubkey, isPrivate),
    removeFollow: async (pubkey: string) => unfollowUser(pubkey),
    getAllFollowsWithStatus: async () => getAllFollowsWithStatus(),

    // Combined follow list
    getCombinedFollowList: async (
      _pubkey: string,
      isInitialSync: boolean = false
    ) => {
      if (isInitialSync) {
        isSyncing = true;
        logger.info('FollowOrchestrator', 'Starting initial sync...');
        AppState.getInstance().setState('user', {
          syncStatus: { status: 'syncing' },
        });
      }

      try {
        // Check if migration needed
        if (!isMigratedToFileStorage()) {
          logger.info(
            'FollowOrchestrator',
            'First run - migrating from relay to file storage...'
          );
          const relayResult = await fetchFromRelays();
          if (relayResult.items.length > 0) {
            setFollowItems(relayResult.items);
            await saveToFile();
          }
          setMigratedToFileStorage();
        }

        // Read from browser storage
        const allFollows = getFollowItems();

        if (isInitialSync) {
          isSyncing = false;
          lastSyncedFollowCount = allFollows.length;
          lastSyncTimestamp = Date.now();
          logger.info(
            'FollowOrchestrator',
            `Initial sync completed: ${allFollows.length} follows`
          );
          AppState.getInstance().setState('user', {
            syncStatus: {
              status: 'synced',
              count: allFollows.length,
              timestamp: lastSyncTimestamp,
            },
          });
        }

        return allFollows;
      } catch (error) {
        if (isInitialSync) {
          isSyncing = false;
          logger.error(
            'FollowOrchestrator',
            `Initial sync failed: ${String(error)}`
          );
          AppState.getInstance().setState('user', {
            syncStatus: { status: 'error', error: String(error) },
          });
        }
        return [];
      }
    },

    // Publish
    publishFollowList: async (
      publicFollows: FollowItem[],
      privateFollows: FollowItem[],
      skipValidation: boolean = false
    ) => {
      const currentUser = AuthService.getInstance().getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated');

      if (isSyncing) throw new Error('Still syncing follow list. Please wait.');

      // Validate dramatic changes
      if (!skipValidation && lastSyncedFollowCount > 0) {
        const newTotal = publicFollows.length + privateFollows.length;
        const percentageChange =
          ((newTotal - lastSyncedFollowCount) / lastSyncedFollowCount) * 100;
        const isDramaticDrop =
          percentageChange < -50 ||
          (lastSyncedFollowCount > 10 && newTotal <= 5);

        if (isDramaticDrop) {
          throw new Error(
            `Suspicious follow count change: ${lastSyncedFollowCount} → ${newTotal}`
          );
        }
      }

      // Write to files first
      await writePublicFollowsFile({
        items: publicFollows,
        lastModified: now(),
      });
      await writePrivateFollowsFile({
        items: privateFollows,
        lastModified: now(),
      });

      // Update browser storage
      const allItems = [
        ...publicFollows.map(f => ({ ...f, isPrivate: false })),
        ...privateFollows.map(f => ({ ...f, isPrivate: true })),
      ];
      setFollowItems(allItems);

      // Publish to relays
      await publishToRelays();

      lastSyncedFollowCount = publicFollows.length + privateFollows.length;
      lastSyncTimestamp = Date.now();

      return true;
    },

    // Fetch from relays (for sync button)
    fetchFollowsFromRelays: async (_pubkey: string) => {
      return fetchFromRelays();
    },

    // Browser storage access
    getBrowserItems: () => getFollowItems(),
    setBrowserItems: (items: FollowItem[]) => setFollowItems(items),

    // File operations
    saveToFile,
    restoreFromFile,

    // Migration methods (public ↔ private)
    migrateToPrivate: async (_pubkey: string) => {
      const items = getFollowItems();
      const updatedItems = items.map(item => ({ ...item, isPrivate: true }));
      setFollowItems(updatedItems);
      await saveToFile();
      await publishToRelays();
      return true;
    },

    migrateToPublic: async (_pubkey: string) => {
      const items = getFollowItems();
      const updatedItems = items.map(item => ({ ...item, isPrivate: false }));
      setFollowItems(updatedItems);
      await saveToFile();
      await publishToRelays();
      return true;
    },
  }),
};

// Also export as FollowListOrchestrator for existing imports
export const FollowListOrchestrator = {
  getInstance: FollowOrchestrator.getInstance,
};

// ============================================================
// PROFILE FOLLOW MANAGER (profile follow/unfollow UI)
// ============================================================

export interface FollowState {
  isFollowing: boolean;
  followingCount: number;
}

export class ProfileFollowManager {
  private authService: AuthService;
  private targetPubkey: string;
  private isFollowingState: boolean = false;

  constructor(targetPubkey: string) {
    this.targetPubkey = targetPubkey;
    this.authService = AuthService.getInstance();
  }

  /**
   * Check if current user follows the target profile
   */
  public async checkFollowStatus(): Promise<boolean> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || this.targetPubkey === currentUser.pubkey) {
      return false;
    }

    // Read from browser storage
    let browserItems = getFollowItems();

    // If empty, try to restore from files
    if (browserItems.length === 0) {
      try {
        const fileItems = await getFileFollows();
        if (fileItems.length > 0) {
          setFollowItems(fileItems);
          browserItems = fileItems;
        }
      } catch {
        // File read failed, continue with empty
      }
    }

    this.isFollowingState = browserItems.some(
      item => item.pubkey === this.targetPubkey
    );
    return this.isFollowingState;
  }

  /**
   * Get current follow status
   */
  public getFollowStatus(): boolean {
    return this.isFollowingState;
  }

  /**
   * Render follow button HTML
   */
  public renderFollowButton(): string {
    const currentUser = this.authService.getCurrentUser();

    // No button for self or when logged out
    if (!currentUser || this.targetPubkey === currentUser.pubkey) {
      return '';
    }

    // Already following - show unfollow button
    if (this.isFollowingState) {
      return `
        <button class="btn btn--passive" data-action="unfollow">
          Unfollow
        </button>
      `;
    }

    // Not following - show follow button (with dropdown if private follows enabled)
    if (!isPrivateFollowsEnabled()) {
      return `
        <button class="btn" data-action="follow">
          Follow
        </button>
      `;
    }

    return `
      <div class="follow-dropdown-container">
        <button class="btn" id="follow-btn-dropdown">
          Follow
          <svg width="12" height="12" style="margin-left: 4px;"><use href="#icon-chevron-down"/></svg>
        </button>
        <div class="follow-dropdown-menu" id="follow-dropdown-menu">
          <button class="follow-dropdown-item" data-action="follow-public">
            <svg width="16" height="16"><use href="#icon-globe"/></svg>
            Follow publicly
          </button>
          <button class="follow-dropdown-item" data-action="follow-private">
            <svg width="16" height="16"><use href="#icon-lock"/></svg>
            Follow privately
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Setup follow button event handlers
   */
  public setupFollowButton(
    container: HTMLElement,
    onStateChange: () => void
  ): void {
    if (!container || !container.isConnected) return;

    // Handle unfollow button
    const unfollowBtn = container.querySelector('[data-action="unfollow"]');
    if (unfollowBtn) {
      unfollowBtn.addEventListener('click', async () => {
        await this.handleUnfollow(container, onStateChange);
      });
      return;
    }

    // Handle simple follow button (NIP-51 disabled)
    const simpleFollowBtn = container.querySelector('[data-action="follow"]');
    if (simpleFollowBtn) {
      simpleFollowBtn.addEventListener('click', async () => {
        await this.handleFollow(container, 'public', onStateChange);
      });
      return;
    }

    // Handle follow dropdown (NIP-51 enabled)
    const dropdownBtn = container.querySelector('#follow-btn-dropdown');
    const dropdownMenu = container.querySelector('#follow-dropdown-menu');

    if (!dropdownBtn || !dropdownMenu) return;

    dropdownBtn.addEventListener('click', e => {
      e.stopPropagation();
      dropdownMenu.classList.toggle('show');
    });

    document.addEventListener('click', () => {
      dropdownMenu.classList.remove('show');
    });

    const dropdownItems = container.querySelectorAll('.follow-dropdown-item');
    dropdownItems.forEach(item => {
      item.addEventListener('click', async e => {
        e.stopPropagation();
        const action = (item as HTMLElement).dataset.action;
        dropdownMenu.classList.remove('show');

        if (action === 'follow-public') {
          await this.handleFollow(container, 'public', onStateChange);
        } else if (action === 'follow-private') {
          await this.handleFollow(container, 'private', onStateChange);
        }
      });
    });
  }

  /**
   * Handle follow action
   */
  private async handleFollow(
    container: HTMLElement,
    type: 'public' | 'private',
    onStateChange: () => void
  ): Promise<void> {
    if (!container || !container.isConnected) return;

    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;

    if (isSyncing) {
      ToastService.show('Still syncing follow list. Please wait.', 'warning');
      return;
    }

    const dropdownBtn = container.querySelector(
      '#follow-btn-dropdown'
    ) as HTMLButtonElement;
    const simpleBtn = container.querySelector(
      '[data-action="follow"]'
    ) as HTMLButtonElement;
    const followBtn = dropdownBtn || simpleBtn;

    if (!followBtn) return;

    const originalHTML = followBtn.innerHTML;

    try {
      followBtn.disabled = true;
      followBtn.textContent = 'Following...';

      followUser(this.targetPubkey, type === 'private');

      this.isFollowingState = true;
      // No manual emit: followUser → setFollowItems already emits follow:updated.
      onStateChange();

      ToastService.show(
        `Followed ${type === 'public' ? 'publicly' : 'privately'} (local)`,
        'success'
      );
    } catch (error) {
      console.error('Failed to follow:', error);
      ToastService.show('Failed to follow user', 'error');

      followBtn.disabled = false;
      followBtn.innerHTML = originalHTML;
      this.setupFollowButton(container, onStateChange);
    }
  }

  /**
   * Handle unfollow action
   */
  private async handleUnfollow(
    container: HTMLElement,
    onStateChange: () => void
  ): Promise<void> {
    if (!container || !container.isConnected) return;

    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;

    if (isSyncing) {
      ToastService.show('Still syncing follow list. Please wait.', 'warning');
      return;
    }

    const followBtn = container.querySelector(
      '[data-action="unfollow"]'
    ) as HTMLButtonElement;
    if (!followBtn) return;

    try {
      followBtn.disabled = true;
      followBtn.textContent = 'Unfollowing...';

      unfollowUser(this.targetPubkey);

      this.isFollowingState = false;
      // No manual emit: unfollowUser → setFollowItems already emits follow:updated.
      onStateChange();

      ToastService.show('Unfollowed successfully (local)', 'success');

      // Reliable propagation: republish follow list IMMEDIATELY, bypassing debounce.
      diagLog('lists', 'immediate publish after unfollow — start', {
        pubkey: this.targetPubkey.slice(0, 8),
      });
      try {
        await publishToRelays();
        diagLog('lists', 'immediate publish after unfollow — done', {
          pubkey: this.targetPubkey.slice(0, 8),
        });
      } catch (pubErr) {
        diagLog('lists', 'immediate publish after unfollow — FAILED', {
          pubkey: this.targetPubkey.slice(0, 8),
          error: String(pubErr),
        });
        logger.warn(
          'follows.ts',
          `Immediate publish after unfollow failed: ${String(pubErr)}`
        );
      }
    } catch (error) {
      console.error('Failed to unfollow:', error);
      ToastService.show('Failed to unfollow user', 'error');

      followBtn.disabled = false;
      followBtn.textContent = 'Unfollow';
    }
  }
}

// ============================================================
// FOLLOW LIST MANAGER (sidebar list UI)
// ============================================================

export class FollowListManager {
  // Base properties
  private eventBus: TypedEventBus;
  private authService: AuthService;
  private containerElement: HTMLElement;
  private infiniteScroll: InfiniteScroll | null = null;
  private allItemsWithProfiles: FollowItemWithProfile[] = [];
  private currentOffset: number = 0;
  private hasMore: boolean = true;
  private isLoading: boolean = false;
  private readonly BATCH_SIZE: number = 20;

  // Follow-specific properties
  private followOrch: ReturnType<typeof FollowListOrchestrator.getInstance>;
  private userProfileService: UserProfileService;
  private extended: FollowsExtendedFeatures | null = null;
  private extendedReady: Promise<void> | null = null;
  private router: Router;
  private adapter: FollowStorageAdapter;

  // Stats and filter
  private totalFollowing: number = 0;
  private showOnlyNonMutuals: boolean = false;

  // Sorting and loading state
  private isFullyLoaded: boolean = false;
  private currentSort: 'date' | 'zaps' = 'date';
  private isLoadingAll: boolean = false;
  private originalOrder: string[] = []; // Store original pubkey order for date sorting
  private usernameFilter: string = ''; // Filter by username
  /**
   * True only while an in-list mutation (e.g. handleRemoveItem) is writing to
   * storage. setFollowItems emits follow:updated synchronously, which would
   * otherwise re-enter refreshListIfActive and trigger a full re-render — but
   * the in-list handler already updates the DOM and state optimistically, so the
   * self-triggered refresh must be suppressed. External follow:updated events
   * (flag false) still refresh normally.
   */
  private isLocalMutation: boolean = false;

  constructor(containerElement: HTMLElement) {
    this.containerElement = containerElement;
    this.eventBus = TypedEventBus.getInstance();
    this.authService = AuthService.getInstance();

    this.adapter = new FollowStorageAdapter();
    this.followOrch = FollowListOrchestrator.getInstance();
    this.userProfileService = UserProfileService.getInstance();
    this.router = Router.getInstance();

    this.extendedReady = this.initExtended();
    this.setupEventListeners();
  }

  /**
   * Initialize extended features (mutuals, zaps, change detection) if addon enabled
   */
  private async initExtended(): Promise<void> {
    if (!isExtendedFollowsEnabled()) return;
    const { FollowsExtendedFeatures } = await import('./follows-extended');
    this.extended = new FollowsExtendedFeatures();
    this.setupExtendedEventListeners();
  }

  /**
   * Setup event listeners (inlined from BaseListManager)
   */
  private setupEventListeners(): void {
    this.eventBus.on('follow:updated', () => this.refreshListIfActive());
    this.eventBus.on('user:logout', () => {
      this.refreshListIfActive();
      this.switchToSystemLogsTab();
    });
    this.eventBus.on('user:login', () => {
      this.resetBatchState();
      this.refreshListIfActive();
      // Follow-specific resets
      this.totalFollowing = 0;
      this.extended?.reset();
      this.isFullyLoaded = false;
      this.originalOrder = [];
      this.usernameFilter = '';
    });
    this.eventBus.on('list-sync-mode:changed', () =>
      this.refreshListIfActive()
    );

    // Extended Follows addon toggle: load/unload extended features and refresh
    this.eventBus.on(
      'extended-follows:toggle',
      async (data: { enabled: boolean }) => {
        if (data.enabled && !this.extended) {
          this.extendedReady = this.initExtended();
          await this.extendedReady;
        } else if (!data.enabled && this.extended) {
          this.extended = null;
          this.extendedReady = null;
        }
        this.refreshListIfActive();
      }
    );
  }

  /**
   * Setup extended features TypedEventBus listeners (called after extended is initialized)
   */
  private setupExtendedEventListeners(): void {
    if (!this.extended) return;
    this.extended.setupEventListeners({
      onZapStatsLoaded: () => {
        this.extended!.updateAllZapBadges(this.containerElement);
        const container = this.containerElement.querySelector(
          '[data-tab-content="list-follows"]'
        ) as HTMLElement;
        if (container) {
          this.updateSortControlsUI(container);
        }
      },
      onMutualChangesUpdate: () => {
        this.extended!.updateGreenDot();
      },
    });
  }

  /**
   * Reset batch loading state (inlined from BaseListManager)
   */
  private resetBatchState(): void {
    this.allItemsWithProfiles = [];
    this.currentOffset = 0;
    this.hasMore = true;
    this.isLoading = false;
  }

  /**
   * Refresh list if it's currently active (inlined from BaseListManager).
   * Skipped while an in-list mutation is in progress — that handler updates the
   * DOM and state itself, so a full re-render (which resets state, flashes
   * "Loading follows..." and re-fetches every profile) would be redundant and
   * racey. External follow:updated events still trigger a refresh.
   */
  private refreshListIfActive(): void {
    if (this.isLocalMutation) return;
    const listTab = this.containerElement.querySelector(
      '[data-tab-content="list-follows"]'
    );
    if (listTab?.classList.contains('tab-content--active')) {
      this.renderListTab(listTab as HTMLElement).catch(err => {
        console.error('Failed to refresh Follows:', err);
      });
    }
  }

  /**
   * Switch to System Logs tab (inlined from BaseListManager)
   */
  private switchToSystemLogsTab(): void {
    switchTabWithContent(this.containerElement, getSccDefaultTab());
  }

  /**
   * Handle load more (infinite scroll trigger) (inlined from BaseListManager)
   */
  private async handleLoadMore(): Promise<void> {
    const list = this.containerElement.querySelector('.follows-list');
    if (!list || this.isLoading || !this.hasMore) return;
    await this.loadBatch(list as HTMLElement);
  }

  /**
   * Render control buttons based on sync mode (inlined from BaseListManager)
   */
  private renderControlButtons(): string {
    return renderListSyncButtons();
  }

  /**
   * Bind sync button handlers (inlined from BaseListManager)
   */
  private bindSyncButtons(container: HTMLElement): void {
    bindListSyncButtons(container, {
      onSyncFromRelays: () => this.handleSyncFromRelays(container),
      onSyncToRelays: () => this.handleSyncToRelays(),
      onSaveToFile: () => this.handleSaveToFile(),
      onRestoreFromFile: () => this.handleRestoreFromFile(container),
      onSwitchMode: () => this.renderListTab(container),
    });
  }

  // ===== Sync Helper Methods (inlined from ListSyncManager) =====

  /**
   * Calculate diff between browser and relay/file items
   */
  private calculateDiff(
    browserItems: FollowItem[],
    relayItems: FollowItem[],
    preservePrivateItems: boolean = false
  ): SyncDiff {
    const browserIds = new Set(browserItems.map(item => item.pubkey));
    const relayIds = new Set(relayItems.map(item => item.pubkey));

    const added = relayItems.filter(item => !browserIds.has(item.pubkey));
    const removed = browserItems.filter(item => {
      if (!relayIds.has(item.pubkey)) {
        if (preservePrivateItems && item.isPrivate === true) {
          return false;
        }
        return true;
      }
      return false;
    });
    const unchanged = browserItems.filter(item => relayIds.has(item.pubkey));

    return { added, removed, unchanged };
  }

  /**
   * Merge browser items with relay/file items (union)
   */
  private mergeFollowItems(
    browserItems: FollowItem[],
    newItems: FollowItem[]
  ): FollowItem[] {
    return mergeByKey(browserItems, newItems, 'pubkey');
  }

  /**
   * Sync from relays - Phase 1: Fetch and compare
   */
  private async syncFromRelays(): Promise<SyncFromRelaysResult> {
    // Snapshot browser state BEFORE fetch (fetch takes 2-10s, user could change list meanwhile)
    const browserItems = this.adapter.getBrowserItems();
    diagLog('lists', 'FollowListManager.syncFromRelays: browserItems', {
      count: browserItems.length,
      pubkeys: browserItems.map(i => i.pubkey.slice(0, 8)),
    });
    const fetchResult = await this.adapter.fetchFromRelays();
    const relayItems = fetchResult.items;
    const relayContentWasEmpty = fetchResult.relayContentWasEmpty;
    const decryptionFailed = fetchResult.decryptionFailed || false;
    diagLog('lists', 'FollowListManager.syncFromRelays: fetchResult', {
      relayItemCount: relayItems.length,
      relayContentWasEmpty,
      decryptionFailed,
    });

    const preservePrivateItems = relayContentWasEmpty || decryptionFailed;
    const diff = this.calculateDiff(
      browserItems,
      relayItems,
      preservePrivateItems
    );

    // Use full state comparison (checks ALL differences including order and properties)
    const requiresConfirmation = hasFollowDifference(browserItems, relayItems);
    diagLog('lists', 'FollowListManager.syncFromRelays: result', {
      requiresConfirmation,
      added: diff.added.length,
      removed: diff.removed.length,
      unchanged: diff.unchanged.length,
      preservePrivateItems,
    });

    return {
      requiresConfirmation,
      diff,
      relayItems,
      relayContentWasEmpty: preservePrivateItems,
      relayTimestamp: fetchResult.relayTimestamp,
    };
  }

  /**
   * Sync from relays - Phase 2: Apply
   */
  private applySyncFromRelays(
    strategy: 'merge' | 'overwrite',
    relayItems: FollowItem[],
    relayContentWasEmpty: boolean = false
  ): void {
    const browserItems = this.adapter.getBrowserItems();

    if (strategy === 'overwrite') {
      if (relayContentWasEmpty) {
        const localPrivateItems = browserItems.filter(
          item => item.isPrivate === true
        );
        this.adapter.setBrowserItems([...relayItems, ...localPrivateItems]);
      } else {
        this.adapter.setBrowserItems(relayItems);
      }
    } else {
      const merged = this.mergeFollowItems(browserItems, relayItems);
      this.adapter.setBrowserItems(merged);
    }
  }

  /**
   * Sync from file - Phase 1: Read and compare
   */
  private async syncFromFile(): Promise<SyncFromFileResult> {
    const fileItems = await this.adapter.getFileItems();
    const browserItems = this.adapter.getBrowserItems();
    const diff = this.calculateDiff(browserItems, fileItems, false);

    // Use full state comparison (checks ALL differences including order and properties)
    const requiresConfirmation = hasFollowDifference(browserItems, fileItems);

    return { requiresConfirmation, diff, fileItems };
  }

  /**
   * Sync from file - Phase 2: Apply
   */
  private applySyncFromFile(
    strategy: 'merge' | 'overwrite',
    fileItems: FollowItem[]
  ): void {
    const browserItems = this.adapter.getBrowserItems();

    if (strategy === 'overwrite') {
      this.adapter.setBrowserItems(fileItems);
    } else {
      const merged = this.mergeFollowItems(browserItems, fileItems);
      this.adapter.setBrowserItems(merged);
    }
  }

  // ===== Sync Button Handlers =====

  /**
   * Handle Sync from Relays
   */
  private async handleSyncFromRelays(container: HTMLElement): Promise<void> {
    try {
      ToastService.show('Fetching from relays...', 'info');
      const result = await this.syncFromRelays();

      if (result.requiresConfirmation) {
        const modal = new SyncConfirmationModal({
          listType: 'Follows',
          added: result.diff.added,
          removed: result.diff.removed,
          getDisplayName: async (item: FollowItem) => {
            const profile = await this.userProfileService.getUserProfile(
              item.pubkey
            );
            return extractDisplayName(profile);
          },
          onKeep: async () => {
            this.applySyncFromRelays(
              'merge',
              result.relayItems,
              result.relayContentWasEmpty
            );
            await this.adapter.publishToRelays(this.adapter.getBrowserItems());
            ToastService.show(
              `Merged ${result.diff.added.length} new follows and synced to relays`,
              'success'
            );
            await this.renderListTab(container);
          },
          onRelay: async () => {
            this.applySyncFromRelays(
              'overwrite',
              result.relayItems,
              result.relayContentWasEmpty
            );
            ToastService.show(
              `Synced from relays (added ${result.diff.added.length}, removed ${result.diff.removed.length})`,
              'success'
            );
            await this.renderListTab(container);
          },
          onLocal: async () => {
            await this.adapter.publishToRelays(this.adapter.getBrowserItems());
            ToastService.show('Local follows pushed to relays', 'success');
            await this.renderListTab(container);
          },
        });
        await modal.show();
      } else {
        this.applySyncFromRelays(
          'merge',
          result.relayItems,
          result.relayContentWasEmpty
        );
        ToastService.show(
          `Synced ${result.diff.added.length} new follow${result.diff.added.length !== 1 ? 's' : ''} from relays`,
          'success'
        );
        await this.renderListTab(container);
      }
    } catch (error) {
      console.error('Failed to sync from relays:', error);
      ToastService.show('Failed to sync from relays', 'error');
    }
  }

  /**
   * Handle Sync to Relays
   */
  private async handleSyncToRelays(): Promise<void> {
    try {
      ToastService.show('Publishing to relays...', 'info');
      const browserItems = this.adapter.getBrowserItems();
      await this.adapter.publishToRelays(browserItems);
      ToastService.show('Follows published successfully', 'success');
    } catch (error) {
      console.error('Failed to publish to relays:', error);
      ToastService.show('Failed to publish to relays', 'error');
    }
  }

  /**
   * Handle Save to File
   */
  private async handleSaveToFile(): Promise<void> {
    try {
      ToastService.show('Saving...', 'info');
      const browserItems = this.adapter.getBrowserItems();

      if (PlatformService.getInstance().isDesktop) {
        await this.adapter.setFileItems(browserItems);
      } else {
        downloadAsJson(browserItems, 'follows');
      }
      ToastService.show('Saved successfully', 'success');
    } catch (error) {
      console.error('Failed to save to file:', error);
      ToastService.show('Failed to save', 'error');
    }
  }

  /**
   * Handle Restore from File
   * In Browser/Mobile: shows file upload dialog
   * In Desktop: reads from local file
   */
  private async handleRestoreFromFile(container: HTMLElement): Promise<void> {
    try {
      let result: SyncFromFileResult;

      if (PlatformService.getInstance().isBrowser) {
        // Browser/Mobile: Upload file via dialog
        const uploadedItems = await uploadJsonFile<FollowItem[]>();
        if (!uploadedItems) {
          return; // User cancelled
        }
        const browserItems = this.adapter.getBrowserItems();
        const diff = this.calculateDiff(browserItems, uploadedItems, false);
        // Use full state comparison
        result = {
          requiresConfirmation: hasFollowDifference(
            browserItems,
            uploadedItems
          ),
          diff,
          fileItems: uploadedItems,
        };
      } else {
        // Desktop: Read from local file
        ToastService.show('Reading from file...', 'info');
        result = await this.syncFromFile();
      }

      if (result.requiresConfirmation) {
        const modal = new SyncConfirmationModal({
          listType: 'Follows (File)',
          added: result.diff.added,
          removed: result.diff.removed,
          getDisplayName: async (item: FollowItem) => {
            const profile = await this.userProfileService.getUserProfile(
              item.pubkey
            );
            return extractDisplayName(profile);
          },
          onKeep: async () => {
            this.applySyncFromFile('merge', result.fileItems);
            await this.adapter.publishToRelays(this.adapter.getBrowserItems());
            ToastService.show(
              `Merged ${result.diff.added.length} from file and synced to relays`,
              'success'
            );
            await this.renderListTab(container);
          },
          onRelay: async () => {
            this.applySyncFromFile('overwrite', result.fileItems);
            ToastService.show(
              `Restored from file (added ${result.diff.added.length}, removed ${result.diff.removed.length})`,
              'success'
            );
            await this.renderListTab(container);
          },
          onLocal: async () => {
            await this.adapter.publishToRelays(this.adapter.getBrowserItems());
            ToastService.show('Local follows pushed to relays', 'success');
            await this.renderListTab(container);
          },
        });
        void modal.show();
      } else if (result.diff.added.length > 0) {
        this.applySyncFromFile('overwrite', result.fileItems);
        ToastService.show(
          `Restored ${result.diff.added.length} item${result.diff.added.length > 1 ? 's' : ''} from file`,
          'success'
        );
        await this.renderListTab(container);
      } else {
        ToastService.show('File is identical to current list', 'info');
      }
    } catch (error) {
      console.error('Failed to restore from file:', error);
      ToastService.show(`Failed to restore: ${String(error)}`, 'error');
    }
  }

  /**
   * Cleanup (inlined from BaseListManager)
   */
  public destroy(): void {
    if (this.infiniteScroll) {
      this.infiniteScroll.destroy();
      this.infiniteScroll = null;
    }
  }

  /**
   * Initialize browser storage (placeholder for consistency)
   */
  private async initializeBrowserStorage(): Promise<void> {
    // No automatic restore - app uses last browser state
  }

  /**
   * Fetch all follows with profiles and mutual status
   */
  private async getAllItemsWithProfiles(): Promise<FollowItemWithProfile[]> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) throw new Error('User not authenticated');

    // Cascading restore: browser → file → relays (if browser empty)
    let allFollows = this.adapter.getBrowserItems();

    if (allFollows.length === 0) {
      // Try file first
      try {
        const fileItems = await this.adapter.getFileItems();
        if (fileItems.length > 0) {
          this.adapter.setBrowserItems(fileItems);
          allFollows = fileItems;
        }
      } catch {
        // File read failed, continue
      }

      // If still empty, try relays
      if (allFollows.length === 0) {
        try {
          const relayResult = await this.adapter.fetchFromRelays();
          if (relayResult.items.length > 0) {
            this.adapter.setBrowserItems(relayResult.items);
            allFollows = relayResult.items;
          }
        } catch {
          // Relay fetch failed, continue with empty
        }
      }
    }

    // Get public/private status from files (fallback for items without browser flag)
    const followStatus = await this.followOrch.getAllFollowsWithStatus();

    // Fetch profiles for all followed users
    const followsWithProfiles: FollowItemWithProfile[] = await Promise.all(
      allFollows.map(async item => {
        // Priority: browser item's isPrivate flag, then file status
        const fileStatus = followStatus.get(item.pubkey);
        const isPrivate =
          item.isPrivate !== undefined
            ? item.isPrivate
            : fileStatus?.private === true;

        return {
          ...item,
          profile: await this.userProfileService.getUserProfile(item.pubkey),
          isPrivate,
          mutualState: 'unknown' as MutualState, // Resolved per batch (Extended Follows)
        };
      })
    );

    // Reverse to show newest first (tag order in Kind 3 = chronological, oldest first)
    followsWithProfiles.reverse();

    // Store total count
    this.totalFollowing = followsWithProfiles.length;

    return followsWithProfiles;
  }

  /**
   * Render list tab content with sticky header, stats and filter
   */
  public async renderListTab(container: HTMLElement): Promise<void> {
    // Wait for extended features to be initialized (if enabled)
    if (this.extendedReady) await this.extendedReady;

    // Initialize browser storage from file on first render
    await this.initializeBrowserStorage();

    // Clean up existing infinite scroll
    if (this.infiniteScroll) {
      this.infiniteScroll.destroy();
      this.infiniteScroll = null;
    }

    // Reset state
    this.allItemsWithProfiles = [];
    this.currentOffset = 0;
    this.hasMore = true;
    this.isLoading = false;
    this.extended?.reset();
    this.isFullyLoaded = false;
    this.currentSort = 'date';
    this.isLoadingAll = false;

    // Clear unseen changes when tab is opened (via extended features)
    this.extended?.clearUnseenChanges();

    try {
      const currentUser = this.authService.getCurrentUser();

      if (!currentUser) {
        container.innerHTML = `
          <div class="follows-list-empty-state">
            <p>Log in to see your follows</p>
          </div>
        `;
        return;
      }

      // Show loading state
      container.innerHTML = `
        <div class="follows-list-loading">
          Loading follows...
        </div>
      `;

      // Fetch all items with profiles
      const itemsWithProfiles = await this.getAllItemsWithProfiles();

      if (itemsWithProfiles.length === 0) {
        container.innerHTML = `${this.renderControlButtons()}
          <div class="follows-list-empty-state">
            <p>No follows yet</p>
          </div>
        `;
        this.bindSyncButtons(container);
        return;
      }

      // Store all items for batch loading
      this.allItemsWithProfiles = itemsWithProfiles;

      // Store original order for date sorting
      this.originalOrder = itemsWithProfiles.map(item => item.pubkey);

      // Render container with sticky header, controls and list
      container.innerHTML = `
        ${this.renderControlButtons()}
        <div class="follows-header l-spread">
          <h2 class="small follows-stats">
            Following: ${this.totalFollowing}${this.extended ? ' | Mutuals: <span class="mutual-count">...</span> (<span class="mutual-percentage">...</span>%)' : ''}
          </h2>
          ${this.extended?.renderCheckForChangesHtml() ?? ''}
        </div>
        <div class="follows-sort-controls">
          <a href="#" class="follows-sort-controls__load-all">Load all</a>
          ${
            this.extended
              ? `
          <span class="follows-sort-controls__sort">
            Sort by:
            <a href="#" class="follows-sort-controls__sort-date follows-sort-controls__link--disabled ${this.currentSort === 'date' ? 'active' : ''}">Date</a>
            /
            <a href="#" class="follows-sort-controls__sort-zaps follows-sort-controls__link--disabled ${this.currentSort !== 'date' ? 'active' : ''}">Zaps</a>
          </span>
          `
              : ''
          }
          <input type="text"
                 class="follows-sort-controls__search ${this.isFullyLoaded ? '' : 'follows-sort-controls__search--disabled'}"
                 placeholder="Filter by name..."
                 ${this.isFullyLoaded ? '' : 'disabled'} />
          ${
            this.extended
              ? `
          <label class="nn-checkbox nn-checkbox--small" data-non-mutuals>
            <input type="checkbox" data-non-mutuals-toggle ${this.showOnlyNonMutuals ? 'checked' : ''} ${this.isFullyLoaded ? '' : 'disabled'}>
            Non-mutuals only
          </label>
          `
              : ''
          }
        </div>
        <div class="follows-list"></div>
        ${this.extended ? '<div class="mutual-changes-modal" style="display: none;"></div>' : ''}
      `;

      // Bind sync button handlers
      this.bindSyncButtons(container);

      // Bind filter toggle
      const filterToggle = container.querySelector(
        '[data-non-mutuals-toggle]'
      ) as HTMLInputElement;
      filterToggle?.addEventListener('change', () => {
        this.showOnlyNonMutuals = filterToggle.checked;
        this.reRenderList(container);
      });

      // Bind sort controls
      this.bindSortControls(container);

      // Bind check for changes link
      this.extended?.bindCheckForChanges(container);

      const list = container.querySelector('.follows-list');
      if (!list) return;

      // Load first batch
      await this.loadBatch(list as HTMLElement);

      // Update stats
      this.updateStats(container);

      // Setup infinite scroll if there are more items
      if (this.hasMore) {
        this.infiniteScroll = new InfiniteScroll(() => this.handleLoadMore(), {
          loadingMessage: 'Loading more follows...',
        });
        this.infiniteScroll.observe(list as HTMLElement);
      }

      // Start loading zap stats asynchronously (don't await)
      const allPubkeys = this.allItemsWithProfiles.map(item => item.pubkey);
      this.extended?.startZapStatsLoading(allPubkeys);
    } catch (error) {
      console.error('Failed to render follows:', error);
      container.innerHTML = `
        <div class="follows-list-empty-state">
          <p>Failed to load follows</p>
        </div>
      `;
    }
  }

  /**
   * Bind "Check for Changes" link handler
   */
  /**
   * Load batch with mutual status check
   */
  private async loadBatch(listElement: HTMLElement): Promise<void> {
    if (this.isLoading || !this.hasMore) return;

    this.isLoading = true;

    if (this.currentOffset > 0 && this.infiniteScroll) {
      this.infiniteScroll.showLoading();
    }

    try {
      // Get next batch
      const batch = this.allItemsWithProfiles.slice(
        this.currentOffset,
        this.currentOffset + this.BATCH_SIZE
      );

      if (batch.length === 0) {
        this.hasMore = false;
        if (this.infiniteScroll) {
          this.infiniteScroll.hideLoading();
        }
        return;
      }

      // Check mutual status for this batch (via extended features)
      if (this.extended) {
        await this.extended.checkMutualStatusBatch(batch);
      }

      // Render batch
      this.renderBatch(listElement, batch);

      // Re-verify any still-'unknown' mutual verdicts in the background so the
      // list converges to the correct state within the session.
      if (this.extended) {
        this.extended.scheduleUnknownReverify(batch, listElement, () => {
          const c = listElement.closest('[data-tab-content]');
          if (c) this.updateStats(c as HTMLElement);
        });
      }

      // Update offset
      this.currentOffset += batch.length;

      // Check if there are more items
      if (this.currentOffset >= this.allItemsWithProfiles.length) {
        this.hasMore = false;
      }

      // Update stats
      const container = listElement.closest(
        '[data-tab-content]'
      ) as HTMLElement;
      if (container) {
        this.updateStats(container);
      }
    } catch (error) {
      console.error('Failed to load batch:', error);
    } finally {
      this.isLoading = false;
      if (this.infiniteScroll) {
        this.infiniteScroll.hideLoading();
      }
    }
  }

  /**
   * Render batch of follow items with mutual badge and zap stats
   */
  private renderBatch(
    listElement: HTMLElement,
    batch: FollowItemWithProfile[]
  ): void {
    const sentinel = listElement.querySelector('.infinite-scroll-sentinel');

    for (const item of batch) {
      if (this.showOnlyNonMutuals && item.mutualState === 'follows') {
        continue;
      }

      const followItemDiv = this.createFollowItemElement(item);

      if (sentinel) {
        listElement.insertBefore(followItemDiv, sentinel);
      } else {
        listElement.appendChild(followItemDiv);
      }
    }
  }

  /**
   * Create a follow item DOM element with event handlers
   */
  private createFollowItemElement(item: FollowItemWithProfile): HTMLElement {
    const username = extractDisplayName(item.profile);
    const npub = hexToNpub(item.pubkey);
    const avatarUrl = item.profile?.picture || '';

    // Private encrypted note (NIP-78) — icon shown when the "Private petnames"
    // setting is on. Empty = peach (--color-5), filled = mint (--color-6).
    const notesEnabled = storage.get<boolean>(
      StorageKeys.PRIVATE_PETNAMES_ENABLED,
      false
    );
    const hasNote = !!(storage.get<Record<string, string>>(
      StorageKeys.PETNAMES,
      {}
    ) ?? {})[item.pubkey];

    const mutualBadgeHtml =
      this.extended?.renderMutualBadge(item.mutualState) ?? '';
    const zapBadgeHtml = this.extended?.renderZapBadge(item.pubkey) ?? '';

    const followItemDiv = document.createElement('div');
    followItemDiv.className = 'ui-list__item follow-item';
    followItemDiv.dataset.pubkey = item.pubkey;
    followItemDiv.innerHTML = `
      <div class="follow-item__content-wrapper">
        <div class="follow-item__avatar">
          <img class="profile-pic profile-pic--medium" src="${escapeHtmlAttr(avatarUrl)}" alt="${escapeHtmlAttr(username)}" />
        </div>
        <div class="follow-item__info">
          <div class="follow-item__username">
            ${escapeHtml(username)}
            ${!item.isPrivate ? `<span class="follow-item__petname-edit" data-role="petname-edit">${item.petname ? `(${escapeHtml(item.petname)})` : '(+)'}</span>` : ''}
            ${notesEnabled ? `<span class="follow-item__note${hasNote ? ' follow-item__note--filled' : ''}" data-role="petname-note" title="${hasNote ? 'Edit private note' : 'Add private note'}"><svg width="16" height="16"><use href="#icon-note"/></svg></span>` : ''}
            ${item.isPrivate ? '<span class="private-badge">🔒 Private</span>' : ''}
            ${this.renderArticleNotifLabel(item.pubkey)}
          </div>
          <div class="follow-item__badges">
            ${mutualBadgeHtml}
            ${zapBadgeHtml}
          </div>
        </div>
      </div>
      <button class="follow-item__unfollow-btn btn btn--passive btn--medium" data-pubkey="${item.pubkey}">
        Unfollow
      </button>
    `;

    const contentWrapper = followItemDiv.querySelector(
      '.follow-item__content-wrapper'
    );
    contentWrapper?.addEventListener('click', () => {
      this.router.navigate(`/profile/${npub}`);
    });

    const unfollowBtn = followItemDiv.querySelector(
      '.follow-item__unfollow-btn'
    );
    unfollowBtn?.addEventListener('click', async e => {
      e.stopPropagation();
      await this.handleRemoveItem(item, followItemDiv);
    });

    // Inline NIP-02 petname (public follows only) — click to set/edit/clear.
    const petnameEdit = followItemDiv.querySelector(
      '[data-role="petname-edit"]'
    );
    petnameEdit?.addEventListener('click', async e => {
      e.stopPropagation(); // don't navigate to the profile
      const { ModalService } = await import('../services/ModalService');
      const current = getFollowPetname(item.pubkey) ?? '';
      const result = await ModalService.getInstance().prompt({
        title: 'Set Petname',
        message:
          '⚠️ Public label, stored in your contact list (NIP-02). Anyone who reads your follow list can see it.',
        defaultValue: current,
        placeholder: 'e.g. Bob from work',
        allowEmpty: true,
      });
      if (result === null) return;
      const trimmed = result.trim();
      await setFollowPetname(item.pubkey, trimmed);
      if (trimmed) item.petname = trimmed;
      else delete item.petname;
      (petnameEdit as HTMLElement).textContent = trimmed
        ? `(${trimmed})`
        : '(+)';
    });

    // Inline private note (NIP-78, encrypted) — click to write/edit/clear.
    const noteEdit = followItemDiv.querySelector('[data-role="petname-note"]');
    noteEdit?.addEventListener('click', async e => {
      e.stopPropagation(); // don't navigate to the profile
      const [{ ModalService }, { PetnameService }] = await Promise.all([
        import('../services/ModalService'),
        import('../services/PetnameService'),
      ]);
      const service = PetnameService.getInstance();
      const current = service.getPetname(item.pubkey) ?? '';
      const result = await ModalService.getInstance().prompt({
        title: 'Private Note',
        message:
          '🔒 Encrypted note about this user. Only you can decrypt and read it.',
        defaultValue: current,
        placeholder: 'Write anything you want to remember about this user…',
        allowEmpty: true,
        multiline: true,
      });
      if (result === null) return;
      const trimmed = result.trim();
      await service.setPetname(item.pubkey, trimmed);
      const el = noteEdit as HTMLElement;
      el.classList.toggle('follow-item__note--filled', !!trimmed);
      el.title = trimmed ? 'Edit private note' : 'Add private note';
    });

    return followItemDiv;
  }

  /**
   * Re-render list (for filter toggle and sorting)
   */
  private reRenderList(container: HTMLElement): void {
    const list = container.querySelector('.follows-list');
    if (!list) return;

    const sentinel = list.querySelector('.infinite-scroll-sentinel');
    list.innerHTML = '';
    if (sentinel) {
      list.appendChild(sentinel);
    }

    for (const item of this.allItemsWithProfiles.slice(0, this.currentOffset)) {
      if (this.showOnlyNonMutuals && item.mutualState === 'follows') {
        continue;
      }

      if (this.usernameFilter) {
        const username = extractDisplayName(item.profile).toLowerCase();
        if (!username.includes(this.usernameFilter)) {
          continue;
        }
      }

      const followItemDiv = this.createFollowItemElement(item);

      if (sentinel) {
        list.insertBefore(followItemDiv, sentinel);
      } else {
        list.appendChild(followItemDiv);
      }
    }
  }

  /**
   * Update stats display (mutual count and percentage only)
   */
  private updateStats(container: HTMLElement): void {
    const mutualCount = this.extended?.mutualCount ?? 0;
    const percentage =
      this.totalFollowing === 0
        ? 0
        : Math.round((mutualCount / this.totalFollowing) * 100);

    const countEl = container.querySelector('.mutual-count');
    const percentEl = container.querySelector('.mutual-percentage');

    if (countEl) countEl.textContent = String(mutualCount);
    if (percentEl) percentEl.textContent = String(percentage);
  }

  /**
   * Handle unfollow (remove item)
   * Updates browser storage (localStorage) - use "Save to file" / "Sync to Relays" to persist
   */
  private async handleRemoveItem(
    item: FollowItemWithProfile,
    itemElement: HTMLElement
  ): Promise<void> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;

    try {
      const currentItems = this.adapter.getBrowserItems();
      const updatedItems = currentItems.filter(
        (f: FollowItem) => f.pubkey !== item.pubkey
      );

      // Suppress the self-triggered refresh while writing to storage: this
      // handler updates the DOM and state optimistically below, so the
      // follow:updated emitted by setBrowserItems must not cause a full
      // re-render. The emit still propagates to other views (PV, external list).
      this.isLocalMutation = true;
      this.adapter.setBrowserItems(updatedItems);
      this.isLocalMutation = false;

      ToastService.show('Unfollowed user', 'success');

      // Optimistic in-place removal — no list reload.
      itemElement.remove();

      if (item.mutualState === 'follows' && this.extended) {
        this.extended.mutualCount--;
      }
      this.totalFollowing--;
      this.allItemsWithProfiles = this.allItemsWithProfiles.filter(
        f => f.pubkey !== item.pubkey
      );

      const container = this.containerElement.querySelector(
        '[data-tab-content="list-follows"]'
      ) as HTMLElement;
      if (container) {
        if (this.extended) {
          this.extended.updateStatsHeader(container, this.totalFollowing);
        }
      }
      // No manual emit: setBrowserItems → setFollowItems already emitted
      // follow:updated (with the guard active), which synced the other views.
    } catch (error) {
      this.isLocalMutation = false;
      console.error('Failed to unfollow user:', error);
      ToastService.show('Failed to unfollow user', 'error');
    }
  }

  /**
   * Handle tab switch (called by MainLayout)
   */
  public handleTabSwitch(tabName: string, content: HTMLElement): void {
    if (tabName === 'follows') {
      this.renderListTab(content).catch(err => {
        console.error('Failed to render follows tab:', err);
      });
    }
  }

  /**
   * Bind sort control event handlers
   */
  private bindSortControls(container: HTMLElement): void {
    // Load all link
    const loadAllLink = container.querySelector(
      '.follows-sort-controls__load-all'
    );
    loadAllLink?.addEventListener('click', e => {
      e.preventDefault();
      void this.handleLoadAll(container);
    });

    // Sort by Date link
    const sortDateLink = container.querySelector(
      '.follows-sort-controls__sort-date'
    );
    sortDateLink?.addEventListener('click', e => {
      e.preventDefault();
      if (this.isFullyLoaded && this.currentSort !== 'date') {
        this.currentSort = 'date';
        this.sortByDate();
        this.updateSortControlsUI(container);
        this.reRenderList(container);
      }
    });

    // Sort by Zaps link (requires both fully loaded AND zap stats loaded)
    const sortZapsLink = container.querySelector(
      '.follows-sort-controls__sort-zaps'
    );
    sortZapsLink?.addEventListener('click', e => {
      e.preventDefault();
      if (
        this.isFullyLoaded &&
        this.extended?.zapStatsLoaded &&
        this.currentSort !== 'zaps'
      ) {
        this.currentSort = 'zaps';
        this.extended.sortByZaps(this.allItemsWithProfiles);
        this.updateSortControlsUI(container);
        this.reRenderList(container);
      }
    });

    // Username filter input
    const searchInput = container.querySelector(
      '.follows-sort-controls__search'
    ) as HTMLInputElement;
    searchInput?.addEventListener('input', () => {
      this.usernameFilter = searchInput.value.toLowerCase();
      this.reRenderList(container);
    });
  }

  /**
   * Load all items in background
   */
  private async handleLoadAll(container: HTMLElement): Promise<void> {
    if (this.isLoadingAll || this.isFullyLoaded) return;

    this.isLoadingAll = true;

    // Update link text to show loading
    const loadAllLink = container.querySelector(
      '.follows-sort-controls__load-all'
    );
    if (loadAllLink) {
      loadAllLink.textContent = 'Loading...';
      loadAllLink.classList.add('follows-sort-controls__load-all--loading');
    }

    // Start progress bar
    const sortControls = container.querySelector(
      '.follows-sort-controls'
    ) as HTMLElement;
    const progressBar = sortControls
      ? new ProgressBarHelper(sortControls)
      : null;
    progressBar?.start();

    const list = container.querySelector('.follows-list') as HTMLElement;
    if (!list) return;

    const totalItems = this.allItemsWithProfiles.length;

    // Load all remaining batches
    while (this.hasMore && !this.isLoading) {
      await this.loadBatch(list);

      // Update progress bar
      if (progressBar && totalItems > 0) {
        const progress = (this.currentOffset / totalItems) * 100;
        progressBar.update(progress);
      }

      // Small delay to prevent UI freezing
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    this.isFullyLoaded = true;
    this.isLoadingAll = false;

    // Complete progress bar with fade-out
    progressBar?.complete();

    // Update UI
    if (loadAllLink) {
      loadAllLink.textContent = 'All loaded';
      loadAllLink.classList.remove('follows-sort-controls__load-all--loading');
      loadAllLink.classList.add('follows-sort-controls__load-all--done');
    }

    // Enable sort controls
    this.updateSortControlsUI(container);
  }

  /**
   * Update sort controls UI state
   */
  private updateSortControlsUI(container: HTMLElement): void {
    const sortDateLink = container.querySelector(
      '.follows-sort-controls__sort-date'
    ) as HTMLElement;
    const sortZapsLink = container.querySelector(
      '.follows-sort-controls__sort-zaps'
    ) as HTMLElement;
    const searchInput = container.querySelector(
      '.follows-sort-controls__search'
    ) as HTMLInputElement;
    const nonMutualsCheckbox = container.querySelector(
      '[data-non-mutuals-toggle]'
    ) as HTMLInputElement | null;

    // Enable Date sort when fully loaded
    if (this.isFullyLoaded) {
      sortDateLink?.classList.remove('follows-sort-controls__link--disabled');

      if (searchInput) {
        searchInput.disabled = false;
        searchInput.classList.remove('follows-sort-controls__search--disabled');
      }
      if (nonMutualsCheckbox) {
        nonMutualsCheckbox.disabled = false;
      }
    }

    // Zaps sort requires both fully loaded AND zap stats loaded
    if (this.isFullyLoaded && this.extended?.zapStatsLoaded) {
      sortZapsLink?.classList.remove('follows-sort-controls__link--disabled');
    }

    sortDateLink?.classList.toggle('active', this.currentSort === 'date');
    sortZapsLink?.classList.toggle('active', this.currentSort === 'zaps');
  }

  /**
   * Sort items by date (original order - newest first)
   */
  private sortByDate(): void {
    // Reset to original order (newest first based on Kind 3 tag order)
    this.allItemsWithProfiles.sort((a, b) => {
      const indexA = this.originalOrder.indexOf(a.pubkey);
      const indexB = this.originalOrder.indexOf(b.pubkey);
      return indexA - indexB;
    });
  }

  /**
   * Render article notification label if user is subscribed
   */
  private renderArticleNotifLabel(pubkey: string): string {
    const articleNotifService = ArticleNotificationService.getInstance();
    if (articleNotifService.isSubscribed(pubkey)) {
      return '<span class="article-notif-label">(Article alerts)</span>';
    }
    return '';
  }
}

// ============================================================
// EXTERNAL FOLLOW LIST MANAGER (read-only view for other users)
// ============================================================

/**
 * Follow item with profile for external user view
 */
interface ExternalFollowItemWithProfile {
  pubkey: string;
  profile: UserProfile;
  isFollowedByMe: boolean;
}

/**
 * ExternalFollowListManager
 * Displays follows of another user (not the current user)
 * Simplified read-only view without sync features
 *
 * Features:
 * - Total count display
 * - Filter by name
 * - Follow button (for users not already followed)
 *
 * NOT included:
 * - Unfollow button (can't unfollow someone else's follows)
 * - Mutuals display
 * - Zap stats
 * - Sort by options
 * - Load all link
 * - Sync buttons
 */
/** What a user-list renders: the target's follows, or who follows the target. */
export type UserListMode = 'follows' | 'followers';

/**
 * Pluggable data source for ExternalFollowListManager. Both modes render the
 * same user rows; they differ only in how the pubkeys are acquired and how the
 * header reads. Followers stream in batch-by-batch (see streamFollowerList).
 */
interface UserListSource {
  loadingLabel: string;
  emptyLabel: string;
  /** Header sentence given the target's display name and the current count. */
  describe(targetName: string, count: number): string;
  /** Acquire pubkeys, streaming each new chunk via onBatch; resolves when done. */
  load(
    targetPubkey: string,
    onBatch: (pubkeys: string[]) => void
  ): Promise<void>;
}

const FOLLOWS_SOURCE: UserListSource = {
  loadingLabel: 'Loading follows...',
  emptyLabel: "This user doesn't follow anyone yet",
  describe: (name, count) =>
    `<strong>${escapeHtml(name)}</strong> follows ${count} ${count === 1 ? 'user' : 'users'}`,
  load: async (targetPubkey, onBatch) => {
    const pubkeys =
      await UserService.getInstance().getUserFollowing(targetPubkey);
    onBatch(pubkeys);
  },
};

const FOLLOWERS_SOURCE: UserListSource = {
  loadingLabel: 'Loading followers...',
  emptyLabel: 'No followers found',
  describe: (name, count) =>
    `${count} ${count === 1 ? 'user follows' : 'users follow'} <strong>${escapeHtml(name)}</strong>`,
  load: (targetPubkey, onBatch) =>
    FollowerCountService.getInstance()
      .streamFollowerList(targetPubkey, onBatch)
      .then(() => undefined),
};

export class ExternalFollowListManager {
  private targetPubkey: string;
  private source: UserListSource;
  private userProfileService: UserProfileService;
  private authService: AuthService;
  private router: Router;
  private containerElement: HTMLElement | null = null;
  private allItemsWithProfiles: ExternalFollowItemWithProfile[] = [];
  private seen: Set<string> = new Set();
  private myFollows: Set<string> = new Set();
  private targetName: string = '';
  private currentOffset: number = 0;
  private hasMore: boolean = true;
  private isLoading: boolean = false;
  private loadComplete: boolean = false;
  private destroyed: boolean = false;
  /** Subscription id for the follow:updated event, used to sync row buttons when
   *  a follow/unfollow happens in another view (e.g. the PV). Off'd in destroy(). */
  private followSubId: string | null = null;
  private batchChain: Promise<void> = Promise.resolve();
  private infiniteScroll: InfiniteScroll | null = null;
  private usernameFilter: string = '';
  private readonly BATCH_SIZE: number = 20;
  /** Rows rendered eagerly before deferring the rest to infinite scroll. */
  private readonly INITIAL_RENDER_CAP: number = 20;

  constructor(targetPubkey: string, source: UserListSource) {
    this.targetPubkey = targetPubkey;
    this.source = source;
    this.userProfileService = UserProfileService.getInstance();
    this.authService = AuthService.getInstance();
    this.router = Router.getInstance();
  }

  /** Build a manager for a target's follows or followers list. */
  static create(
    targetPubkey: string,
    mode: UserListMode
  ): ExternalFollowListManager {
    return new ExternalFollowListManager(
      targetPubkey,
      mode === 'followers' ? FOLLOWERS_SOURCE : FOLLOWS_SOURCE
    );
  }

  /**
   * Render list tab content. Opens immediately and fills progressively as the
   * source streams pubkeys in, so a slow followers sweep shows results as they
   * arrive instead of blocking on a multi-second spinner.
   */
  public async renderListTab(container: HTMLElement): Promise<void> {
    this.containerElement = container;

    // Reset state
    this.allItemsWithProfiles = [];
    this.seen = new Set();
    this.myFollows = new Set();
    this.currentOffset = 0;
    this.hasMore = true;
    this.isLoading = false;
    this.loadComplete = false;
    this.destroyed = false;
    this.batchChain = Promise.resolve();
    this.usernameFilter = '';

    // Cleanup existing infinite scroll
    if (this.infiniteScroll) {
      this.infiniteScroll.destroy();
      this.infiniteScroll = null;
    }

    // Show loading state
    container.innerHTML = `
      <div class="follows-list-loading">
        ${escapeHtml(this.source.loadingLabel)}
      </div>
    `;

    try {
      // Resolve target name + my follow set up front (header + follow-state).
      const targetProfile = await this.userProfileService.getUserProfile(
        this.targetPubkey
      );
      this.targetName = extractDisplayName(targetProfile);
      this.myFollows = new Set(getFollowItems().map(f => f.pubkey));

      if (this.destroyed) return;

      // Render the shell so rows can stream into the list.
      container.innerHTML = `
        <div class="external-follows-header">
          <div class="external-follows-stats">${this.source.describe(this.targetName, 0)}</div>
          <input type="text"
                 class="external-follows-search"
                 placeholder="Filter by name..." />
        </div>
        <div class="follows-list external-follows-list">
          <div class="follows-list-loading">${escapeHtml(this.source.loadingLabel)}</div>
        </div>
      `;

      // Bind filter input
      const searchInput = container.querySelector(
        '.external-follows-search'
      ) as HTMLInputElement;
      searchInput?.addEventListener('input', () => {
        this.usernameFilter = searchInput.value.toLowerCase();
        this.reRenderList();
      });

      // Keep row buttons in sync with follow/unfollow actions performed in
      // other views (PV, another tab, extended-follows list).
      if (!this.followSubId) {
        this.followSubId = eventBus.on('follow:updated', () =>
          this.syncItemButtons()
        );
      }

      // Stream pubkeys in. Chain the (async) batch handlers so profile
      // resolution and rendering stay serialized — concurrent batches would
      // race the isLoading guard and drop rows.
      await this.source.load(this.targetPubkey, pubkeys => {
        this.batchChain = this.batchChain
          .then(() => this.onBatch(pubkeys))
          .catch(error =>
            console.error('Failed to render user-list batch:', error)
          );
      });

      // Drain in-flight batch handlers, then settle the final state.
      this.loadComplete = true;
      await this.batchChain;
      if (this.destroyed) return;
      this.finalize();
    } catch (error) {
      console.error('Failed to load external user list:', error);
      container.innerHTML = `
        <div class="follows-list-empty-state">
          <p>Failed to load</p>
        </div>
      `;
    }
  }

  /**
   * Handle one streamed chunk of pubkeys: dedupe, resolve profiles, append, and
   * render eagerly up to the initial cap (the rest is left to infinite scroll).
   *
   * Profiles are resolved in BATCH_SIZE slices and rendered progressively so the
   * first INITIAL_RENDER_CAP rows appear after only a small number of profile
   * fetches — instead of awaiting the whole relay batch (which can carry
   * hundreds of pubkeys) before anything paints. Later slices keep resolving
   * in the background to prefetch profiles for smooth scrolling; if the tab is
   * closed mid-stream, the lifecycle destroy() short-circuits the loop.
   */
  private async onBatch(pubkeys: string[]): Promise<void> {
    if (this.destroyed) return;

    const fresh = pubkeys.filter(pk => !this.seen.has(pk));
    if (fresh.length === 0) return;
    fresh.forEach(pk => this.seen.add(pk));

    for (let i = 0; i < fresh.length; i += this.BATCH_SIZE) {
      if (this.destroyed) return;

      const slice = fresh.slice(i, i + this.BATCH_SIZE);
      const items: ExternalFollowItemWithProfile[] = await Promise.all(
        slice.map(async pubkey => ({
          pubkey,
          profile: await this.userProfileService.getUserProfile(pubkey),
          isFollowedByMe: this.myFollows.has(pubkey),
        }))
      );

      if (this.destroyed) return;
      this.allItemsWithProfiles.push(...items);

      // Live count in the header.
      const stats = this.containerElement?.querySelector(
        '.external-follows-stats'
      );
      if (stats)
        stats.innerHTML = this.source.describe(
          this.targetName,
          this.allItemsWithProfiles.length
        );

      // Render whatever fits the initial cap; once capped this is a cheap no-op
      // and just keeps the infinite-scroll sentinel attached.
      await this.renderPending();
    }
  }

  /**
   * Render newly available items up to INITIAL_RENDER_CAP, then attach infinite
   * scroll (once) so the remainder loads on demand.
   */
  private async renderPending(): Promise<void> {
    const list = this.containerElement?.querySelector(
      '.follows-list'
    ) as HTMLElement | null;
    if (!list) return;

    while (
      this.currentOffset < this.INITIAL_RENDER_CAP &&
      this.currentOffset < this.filteredItems().length
    ) {
      await this.loadBatch(list);
    }

    if (!this.infiniteScroll && this.currentOffset > 0) {
      this.infiniteScroll = new InfiniteScroll(() => this.handleLoadMore(), {
        loadingMessage: 'Loading more...',
      });
      this.infiniteScroll.observe(list);
    }
  }

  /** Items matching the current username filter (profiles already resolved). */
  private filteredItems(): ExternalFollowItemWithProfile[] {
    if (!this.usernameFilter) return this.allItemsWithProfiles;
    return this.allItemsWithProfiles.filter(item => {
      const username = extractDisplayName(item.profile).toLowerCase();
      return username.includes(this.usernameFilter);
    });
  }

  /** Streaming finished: show empty state if nothing came back, else settle hasMore. */
  private finalize(): void {
    if (this.allItemsWithProfiles.length === 0) {
      if (this.containerElement) {
        this.containerElement.innerHTML = `
          <div class="follows-list-empty-state">
            <p>${escapeHtml(this.source.emptyLabel)}</p>
          </div>
        `;
      }
      return;
    }
    this.hasMore = this.currentOffset < this.filteredItems().length;
  }

  /**
   * Handle infinite scroll load more
   */
  private async handleLoadMore(): Promise<void> {
    const list = this.containerElement?.querySelector('.follows-list');
    if (!list || this.isLoading || !this.hasMore) return;
    await this.loadBatch(list as HTMLElement);
  }

  /**
   * Render the next slice of filtered items. Streaming-aware: an empty slice
   * only ends the list once the source has finished (loadComplete), so a gap
   * between relay batches doesn't prematurely stop infinite scroll.
   */
  private async loadBatch(listElement: HTMLElement): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;

    if (this.currentOffset > 0 && this.infiniteScroll) {
      this.infiniteScroll.showLoading();
    }

    try {
      const filteredItems = this.filteredItems();

      // Get next batch
      const batch = filteredItems.slice(
        this.currentOffset,
        this.currentOffset + this.BATCH_SIZE
      );

      if (batch.length === 0) {
        // Nothing queued right now — only truly done once streaming completed.
        this.hasMore = !this.loadComplete;
        return;
      }

      // Drop the initial pulsating loader once the first rows arrive.
      listElement.querySelector('.follows-list-loading')?.remove();

      // Render batch
      this.renderBatch(listElement, batch);

      // Update offset
      this.currentOffset += batch.length;

      // More to show if items remain, or more may still stream in.
      this.hasMore =
        this.currentOffset < filteredItems.length || !this.loadComplete;
    } finally {
      this.isLoading = false;
      if (this.infiniteScroll) {
        this.infiniteScroll.hideLoading();
      }
    }
  }

  /**
   * Render batch of items
   */
  private renderBatch(
    listElement: HTMLElement,
    batch: ExternalFollowItemWithProfile[]
  ): void {
    const sentinel = listElement.querySelector('.infinite-scroll-sentinel');

    for (const item of batch) {
      const itemEl = this.createFollowItemElement(item);

      if (sentinel) {
        listElement.insertBefore(itemEl, sentinel);
      } else {
        listElement.appendChild(itemEl);
      }
    }
  }

  /**
   * Create follow item DOM element
   */
  private createFollowItemElement(
    item: ExternalFollowItemWithProfile
  ): HTMLElement {
    const username = extractDisplayName(item.profile);
    const npub = hexToNpub(item.pubkey);
    const avatarUrl = item.profile?.picture || '';
    const isMe = this.authService.isCurrentUser(item.pubkey);

    const itemDiv = document.createElement('div');
    itemDiv.className = 'ui-list__item follow-item external-follow-item';
    itemDiv.dataset.pubkey = item.pubkey;

    itemDiv.innerHTML = `
      <div class="follow-item__content-wrapper">
        <div class="follow-item__avatar">
          <img class="profile-pic profile-pic--medium" src="${escapeHtmlAttr(avatarUrl)}" alt="${escapeHtmlAttr(username)}" />
        </div>
        <div class="follow-item__info">
          <div class="follow-item__username">${escapeHtml(username)}</div>
        </div>
      </div>
    `;

    // Append the follow/unfollow control. Actionable in BOTH states (Follow ↔
    // Unfollow) so the user can toggle without leaving the list, and consistent
    // with the PV which also shows an Unfollow button. Skipped for self / logged-out.
    if (!isMe && this.authService.getCurrentUser()) {
      itemDiv.appendChild(
        item.isFollowedByMe
          ? this.createUnfollowButton(item)
          : this.createFollowButton(item)
      );
    }

    // Navigate to profile on click
    const contentWrapper = itemDiv.querySelector(
      '.follow-item__content-wrapper'
    );
    contentWrapper?.addEventListener('click', () => {
      this.router.navigate(`/profile/${npub}`);
    });

    return itemDiv;
  }

  /**
   * Build the "Follow" action button with its click handler bound.
   */
  private createFollowButton(
    item: ExternalFollowItemWithProfile
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'external-follow-item__follow-btn btn btn--medium';
    btn.dataset.pubkey = item.pubkey;
    btn.textContent = 'Follow';
    btn.addEventListener('click', async e => {
      e.stopPropagation();
      await this.handleFollow(item, btn);
    });
    return btn;
  }

  /**
   * Build the "Unfollow" action button with its click handler bound. Shown when
   * already following so the row stays actionable and consistent with the PV
   * (which also shows an Unfollow button), instead of a static "Following" label.
   */
  private createUnfollowButton(
    item: ExternalFollowItemWithProfile
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className =
      'external-follow-item__unfollow-btn btn btn--medium btn--passive';
    btn.dataset.pubkey = item.pubkey;
    btn.textContent = 'Unfollow';
    btn.addEventListener('click', async e => {
      e.stopPropagation();
      await this.handleUnfollow(item, btn);
    });
    return btn;
  }

  /**
   * Handle follow action: follow locally, then swap the button to Unfollow.
   * followUser → setFollowItems emits follow:updated, which syncItemButtons()
   * uses to keep other open views (e.g. the PV) in lockstep.
   */
  private async handleFollow(
    item: ExternalFollowItemWithProfile,
    followBtn: HTMLButtonElement
  ): Promise<void> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;

    try {
      followBtn.disabled = true;
      followBtn.textContent = 'Following...';

      // Set state BEFORE followUser: followUser → setFollowItems emits
      // follow:updated synchronously, which runs syncItemButtons. With the state
      // already updated, syncItemButtons sees this row as in-sync and skips it,
      // leaving this handler to perform the single button swap below.
      item.isFollowedByMe = true;
      followUser(item.pubkey, false);

      followBtn.replaceWith(this.createUnfollowButton(item));

      ToastService.show('Followed (local)', 'success');
    } catch (error) {
      console.error('Failed to follow:', error);
      ToastService.show('Failed to follow', 'error');
      item.isFollowedByMe = false;
      followBtn.disabled = false;
      followBtn.textContent = 'Follow';
    }
  }

  /**
   * Handle unfollow action: unfollow locally, then swap the button to Follow.
   */
  private async handleUnfollow(
    item: ExternalFollowItemWithProfile,
    unfollowBtn: HTMLButtonElement
  ): Promise<void> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;

    try {
      unfollowBtn.disabled = true;
      unfollowBtn.textContent = 'Unfollowing...';

      // Set state BEFORE unfollowUser for the same reason as handleFollow:
      // unfollowUser emits follow:updated synchronously; syncItemButtons must
      // see this row already in-sync and skip, so this handler swaps once.
      item.isFollowedByMe = false;
      unfollowUser(item.pubkey);

      unfollowBtn.replaceWith(this.createFollowButton(item));

      ToastService.show('Unfollowed (local)', 'success');
    } catch (error) {
      console.error('Failed to unfollow:', error);
      ToastService.show('Failed to unfollow', 'error');
      item.isFollowedByMe = true;
      unfollowBtn.disabled = false;
      unfollowBtn.textContent = 'Unfollow';
    }
  }

  /**
   * Re-sync each rendered row's follow/unfollow button to the current follow
   * state, so a follow/unfollow from ANOTHER view (e.g. the PV, another tab, or
   * the extended-follows list) is reflected here immediately. Rebuilds the
   * myFollows set from storage and only touches rows whose state actually
   * changed; rows that already match (e.g. the row just toggled locally) are
   * skipped, avoiding redundant DOM swaps.
   */
  private syncItemButtons(): void {
    if (this.destroyed || !this.containerElement) return;
    this.myFollows = new Set(getFollowItems().map(f => f.pubkey));
    for (const item of this.allItemsWithProfiles) {
      const nowFollowing = this.myFollows.has(item.pubkey);
      if (nowFollowing === item.isFollowedByMe) continue;
      item.isFollowedByMe = nowFollowing;
      const row = this.containerElement.querySelector<HTMLElement>(
        `.external-follow-item[data-pubkey="${item.pubkey}"]`
      );
      if (!row) continue;
      const oldControl = row.querySelector(
        '.external-follow-item__follow-btn, .external-follow-item__unfollow-btn, .external-follow-item__status'
      );
      if (!oldControl) continue;
      oldControl.replaceWith(
        nowFollowing
          ? this.createUnfollowButton(item)
          : this.createFollowButton(item)
      );
    }
  }

  /**
   * Re-render list (for filter changes)
   */
  private reRenderList(): void {
    const list = this.containerElement?.querySelector('.follows-list');
    if (!list) return;

    // Clear list but keep sentinel
    const sentinel = list.querySelector('.infinite-scroll-sentinel');
    list.innerHTML = '';
    if (sentinel) {
      list.appendChild(sentinel);
    }

    // Reset render position and re-render from the top with the active filter.
    this.currentOffset = 0;
    this.hasMore = true;
    void this.renderPending();
  }

  /**
   * Cleanup
   */
  public destroy(): void {
    this.destroyed = true;
    if (this.followSubId) {
      eventBus.off(this.followSubId);
      this.followSubId = null;
    }
    if (this.infiniteScroll) {
      this.infiniteScroll.destroy();
      this.infiniteScroll = null;
    }
  }
}
